import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Check, FileText, Lightbulb, Paperclip, RefreshCw, Search, Send, Settings, Sparkles, Square, Trash2, X, Download,
} from 'lucide-react';
import type { HarnessEvent } from '@xiaotianshu/shared/harness';
import { useWorkspace } from '../workspace/WorkspaceContext';
import {
  abortAgentTurn, acceptDraft, analyzeBookStyle, answerAgentApproval, approveIdea, archiveAgentMemory,
  createAgentMemory, createIdea, createPairingCode, deleteDraft, deleteIdea, ensureThread, getAgentBudget, getBookStyle,
  getThreadMessages, listAgentMemories, listDrafts, listIdeas, listPersonas,
  openAgentStream, readWeb, reloadThread, rejectDraft, rejectIdea, sendAgentMessage,
  triggerProactive, updateAgentBudget, updateBookStyle, updateDraft, updatePersona,
  type AgentDraft, type AgentDocument, type AgentIdea, type AgentImage, type AgentMemory, type AgentMessage, type AgentPersona,
  type AgentThread, type BudgetStatus, type PairingCode,
} from '../../agent/agentClient';
import './resident.css';
import { ResizeHandle } from './ResizeHandle';
import { LinkifiedText } from '../../components/chat/LinkPreviewCard';
import { ApprovalDetails } from '../../components/chat/ApprovalDetails';
import { ImageViewer } from '../../components/chat/ImageViewer';
import { saveChatFile } from '../../utils/download';
import { fileToChatImage } from '../../utils/chatImage';
import { fileToChatDocument } from '../../utils/chatFile';
import { useAutoGrow } from '../../hooks/useAutoGrow';
import { dataCache } from '../../cache/DataCache';

type SettingsTab = 'persona' | 'style' | 'memory' | 'budget' | 'phone' | 'actions';
type ApprovalEvent = Extract<HarnessEvent, { type: 'approval-request' }>;
type FeedFilter = 'all' | 'idea' | 'draft' | 'open' | 'rejected';

const SIDE_WIDTH_KEY = 'xiaotianshu:resident-side-width:v2';
const SIDE_WIDTH_DEFAULT = 280;
function clampSideWidth(value: number): number {
  return Math.max(180, Math.min(420, Math.round(value)));
}

function activityForTool(name: string): string {
  if (name === 'search_web') return '正在搜索网页…';
  if (name === 'browse_web') return '正在读网页…';
  if (name === 'see_image') return '正在看图片…';
  if (name === 'share_image') return '正在整理图片…';
  if (name === 'bookmark_web') return '正在收藏链接…';
  if (name === 'recall' || name === 'remember') return '正在回忆…';
  if (name === 'current_time') return '正在看时间…';
  if (name === 'propose_idea' || name === 'write_draft' || name === 'edit_chapter') return '正在整理想法…';
  if (name === 'execute') return '正在执行操作…';
  return '正在处理…';
}

function time(value: string | null): string {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString('zh-CN', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
  } catch { return ''; }
}

function shortTime(value: string | Date | null): string {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString('zh-CN', {
      month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
    });
  } catch { return ''; }
}

function rejectionReason(idea: AgentIdea): string {
  const marker = '【作者拒绝】';
  const index = idea.rationale.lastIndexOf(marker);
  return index >= 0 ? idea.rationale.slice(index + marker.length).trim() : '';
}

/** 创作页右侧的余白：聊天、想法、草案、设置全部在这里，不再有独立 /studio 入口。 */
export function ResidentPanel(): React.ReactElement {
  const {
    bookId, bookTitle, chaptersMap, volumes, loadVolumes, selectedChapter, handleSelectChapter, triggerDataRefresh,
  } = useWorkspace();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [headHeight, setHeadHeight] = useState(44);
  const [thread, setThread] = useState<AgentThread | null>(null);
  const [messages, setMessages] = useState<AgentMessage[]>([]);
  const [streaming, setStreaming] = useState('');
  const [activity, setActivity] = useState('');
  const [imageViewer, setImageViewer] = useState<{ src: string; name?: string } | null>(null);
  const [input, setInput] = useState('');
  const [pendingImages, setPendingImages] = useState<AgentImage[]>([]);
  const [pendingDocuments, setPendingDocuments] = useState<AgentDocument[]>([]);
  const inputRef = useAutoGrow(input);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [ideas, setIdeas] = useState<AgentIdea[]>([]);
  const [drafts, setDrafts] = useState<AgentDraft[]>([]);
  const [persona, setPersona] = useState<AgentPersona | null>(null);
  const [budget, setBudget] = useState<BudgetStatus | null>(null);
  const [memories, setMemories] = useState<AgentMemory[]>([]);
  const [pairCode, setPairCode] = useState<PairingCode | null>(null);
  const [approval, setApproval] = useState<ApprovalEvent | null>(null);
  const [busy, setBusy] = useState(false);
  const [settingsTab, setSettingsTab] = useState<SettingsTab>('persona');
  const [personaDraft, setPersonaDraft] = useState({ name: '', nickname: '', identity: '', voice: '', aesthetics: '', boundaries: '' });
  const [styleDraft, setStyleDraft] = useState<Record<string, string>>({});
  const [memoryText, setMemoryText] = useState('');
  const [memoryQuery, setMemoryQuery] = useState('');
  const [draftEdits, setDraftEdits] = useState<Record<string, string>>({});
  const [rejectingIdeaId, setRejectingIdeaId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectingDraftId, setRejectingDraftId] = useState<string | null>(null);
  const [draftRejectText, setDraftRejectText] = useState('');
  const [sideWidth, setSideWidth] = useState(() => {
    const saved = Number(localStorage.getItem(SIDE_WIDTH_KEY));
    return Number.isFinite(saved) && saved > 0 ? clampSideWidth(saved) : SIDE_WIDTH_DEFAULT;
  });
  const [showJump, setShowJump] = useState(false);
  const [highlightIdeaId, setHighlightIdeaId] = useState<string | null>(null);
  const [expandedCardId, setExpandedCardId] = useState<string | null>(null);
  const [feedQuery, setFeedQuery] = useState('');
  const [feedFilter, setFeedFilter] = useState<FeedFilter>('all');
  const [deletingCardId, setDeletingCardId] = useState<string | null>(null);
  const messagesRef = useRef<HTMLDivElement | null>(null);
  const sentAtRef = useRef(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const atBottomRef = useRef(true);
  const streamRef = useRef<(() => void) | null>(null);

  const refreshIdeas = useCallback(async (id: string) => {
    try { setIdeas(await listIdeas(id)); } catch { /* ignore */ }
  }, []);
  const refreshDrafts = useCallback(async (id: string) => {
    try { setDrafts(await listDrafts(id)); } catch { /* ignore */ }
  }, []);

  const fillPersona = (p: AgentPersona) => {
    setPersonaDraft({
      name: p.name, nickname: p.nickname ?? '', identity: p.identity,
      voice: p.voice, aesthetics: p.aesthetics, boundaries: p.boundaries,
    });
  };
  const fillStyle = (s: Record<string, unknown>) => {
    const str = (v: unknown) => typeof v === 'string' ? v : Array.isArray(v) ? v.join('\n') : '';
    setStyleDraft({
      pov: str(s.pov), tense: str(s.tense), paragraph: str(s.paragraph),
      dialogue: str(s.dialogue), vocabulary: str(s.vocabulary), banned: str(s.banned), samples: str(s.samples),
    });
  };

  const attachStream = useCallback((t: AgentThread) => {
    streamRef.current?.();
    streamRef.current = openAgentStream(t.id, (ev: HarnessEvent) => {
      if (ev.type === 'text-delta') {
        setStreaming((v) => v + ev.text);
        setRunning(true);
        setActivity('正在写…');
      } else if (ev.type === 'tool-call') {
        setRunning(true);
        setActivity(activityForTool(ev.name));
      } else if (ev.type === 'tool-result') {
        setActivity('处理完了，继续…');
      } else if (ev.type === 'turn-start') {
        setRunning(true);
        setActivity('正在想…');
      } else if (ev.type === 'assistant-message') {
        setStreaming('');
        if (ev.text.trim()) setMessages((prev) => [...prev, {
          id: `${Date.now()}-${Math.random()}`, role: 'assistant', text: ev.text, created_at: new Date().toISOString(),
        }]);
      } else if (ev.type === 'turn-end') {
        setRunning(false);
        setActivity('');
        setStreaming('');
        void refreshIdeas(bookId);
        void refreshDrafts(bookId);
        void getThreadMessages(t.id).then((rows) => setMessages(rows)).catch(() => { /* 忽略单次刷新失败 */ });
      } else if (ev.type === 'approval-request') {
        setActivity('等你确认…');
        setApproval(ev);
      } else if (ev.type === 'approval-answered') {
        setApproval((prev) => prev?.requestId === ev.requestId ? null : prev);
        setActivity('已经确认，继续…');
      } else if (ev.type === 'status' && ev.status === 'idle') {
        setRunning(false);
        setActivity('');
      } else if (ev.type === 'error') {
        setError(ev.message);
        setActivity('');
      }
    }, (msg) => setError(msg), { replay: false });
  }, [bookId, refreshIdeas, refreshDrafts]);

  useEffect(() => {
    if (!bookId) return;
    let cancelled = false;
    setThread(null);
    setMessages([]);
    setStreaming('');
    setError('');
    atBottomRef.current = true;
    setShowJump(false);
    streamRef.current?.();
    streamRef.current = null;
    void (async () => {
      try {
        const [t, personas, style, b] = await Promise.all([
          ensureThread(bookId),
          listPersonas().catch((): AgentPersona[] => []),
          getBookStyle(bookId).catch((): Record<string, unknown> => ({})),
          getAgentBudget(bookId).catch((): BudgetStatus | null => null),
        ]);
        if (cancelled) return;
        setThread(t);
        setPersona(personas[0] ?? null);
        if (personas[0]) fillPersona(personas[0]);
        fillStyle(style);
        setBudget(b);
        const [msgs] = await Promise.all([
          getThreadMessages(t.id), refreshIdeas(bookId), refreshDrafts(bookId),
        ]);
        if (cancelled) return;
        setMessages(msgs);
        attachStream(t);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : '启动失败');
      }
    })();
    return () => {
      cancelled = true;
      streamRef.current?.();
      streamRef.current = null;
    };
  }, [bookId, refreshIdeas, refreshDrafts, attachStream]);

  useEffect(() => {
    localStorage.setItem(SIDE_WIDTH_KEY, String(sideWidth));
  }, [sideWidth]);

  // 标题栏高度不写死：直接量编辑区工具栏，窗口/缩放变了也跟着变。
  // ⚠️ 必须用 offsetHeight（布局 px），不能用 getBoundingClientRect().height：
  // #root 有 CSS zoom 时后者是视觉 px，拿来写 CSS height 会被再缩放一次。
  useEffect(() => {
    const toolbar = document.querySelector<HTMLElement>('.editor-toolbar');
    if (!toolbar) return;
    const update = () => setHeadHeight(toolbar.offsetHeight || 44);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(toolbar);
    window.addEventListener('resize', update);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  const scrollToLatest = (smooth = true) => {
    const el = messagesRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
    atBottomRef.current = true;
    setShowJump(false);
  };

  const onMessagesScroll = () => {
    const el = messagesRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
    atBottomRef.current = atBottom;
    setShowJump(!atBottom);
  };

  // 点击卡片展开/收起；点卡片里的按钮、输入框不触发。
  const toggleCard = (id: string, event: React.MouseEvent<HTMLElement>) => {
    const target = event.target as HTMLElement;
    if (target.closest('button, textarea, input, select, a, summary')) return;
    setExpandedCardId((prev) => prev === id ? null : id);
  };

  useEffect(() => {
    if (atBottomRef.current) {
      const id = requestAnimationFrame(() => scrollToLatest(false));
      return () => cancelAnimationFrame(id);
    }
    setShowJump(true);
    return undefined;
  }, [messages, streaming]);

  useEffect(() => {
    if (!running || !thread) return;
    let cancelled = false;
    const check = async () => {
      try {
        const latest = await getThreadMessages(thread.id);
        if (cancelled) return;
        setMessages((prev) => {
          const serverIds = new Set(latest.map((m) => m.id));
          const pendingLocals = prev.filter((m) => m.id.startsWith('local-')
            && !latest.some((s) => s.role === 'user' && s.text === m.text));
          return [...latest, ...pendingLocals.filter((m) => !serverIds.has(m.id))];
        });
        const answered = latest.some((m) => m.role === 'assistant' && !m.shared
          && m.created_at && Date.parse(m.created_at) >= sentAtRef.current);
        if (answered) {
          setRunning(false);
          setActivity('');
          setStreaming('');
        }
      } catch { /* SSE 正常时轮询失败不影响 */ }
    };
    const timer = setInterval(() => void check(), 2500);
    return () => { cancelled = true; clearInterval(timer); };
  }, [running, thread]);

  const addFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    try {
      const images = [...pendingImages];
      const docs = [...pendingDocuments];
      for (const file of Array.from(files)) {
        if (/^image\/(png|jpeg|webp|gif)$/i.test(file.type) && images.length < 4) {
          images.push(await fileToChatImage(file));
        } else if (docs.length < 4) {
          docs.push(await fileToChatDocument(file));
        }
      }
      setPendingImages(images);
      setPendingDocuments(docs);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : '附件读取失败');
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const send = async () => {
    const text = input.trim();
    const images = pendingImages;
    const documents = pendingDocuments;
    if ((!text && images.length === 0 && documents.length === 0) || !thread || running) return;
    setInput('');
    setPendingImages([]);
    setPendingDocuments([]);
    setRunning(true);
    setActivity('正在想…');
    sentAtRef.current = Date.now();
    setError('');
    atBottomRef.current = true;
    setShowJump(false);
    setMessages((prev) => [...prev, {
      id: `local-${Date.now()}`, role: 'user', text, images, documents, created_at: new Date().toISOString(),
    }]);
    try { await sendAgentMessage(thread.id, text, images, documents); } catch (e) {
      setRunning(false);
      setActivity('');
      setError(e instanceof Error ? e.message : '发送失败');
    }
  };

  const stop = async () => {
    if (!thread) return;
    try { await abortAgentTurn(thread.id); } catch { /* ignore */ }
    setRunning(false);
    setActivity('');
    setStreaming('');
  };

  const approve = async (idea: AgentIdea) => {
    try { await approveIdea(idea.id); await refreshIdeas(idea.book_id); } catch (e) { setError(String(e)); }
  };
  const beginRejectIdea = (idea: AgentIdea) => {
    setRejectingIdeaId(idea.id);
    setRejectReason('');
  };
  const confirmRejectIdea = async (idea: AgentIdea) => {
    setBusy(true);
    try {
      await rejectIdea(idea.id, rejectReason.trim());
      setRejectingIdeaId(null);
      setRejectReason('');
      await refreshIdeas(idea.book_id);
    } catch (e) { setError(String(e)); } finally { setBusy(false); }
  };
  const addMessageToIdea = async (messageText: string) => {
    if (!bookId) return;
    const selected = window.getSelection()?.toString().trim() ?? '';
    const text = selected && messageText.includes(selected) ? selected : messageText;
    const firstLine = text.split('\n').map((line) => line.trim()).find(Boolean) ?? '来自对话的想法';
    setBusy(true);
    try {
      const idea = await createIdea({
        bookId,
        threadId: thread?.id,
        personaId: persona?.id,
        kind: 'other',
        title: firstLine.slice(0, 80),
        summary: text.slice(0, 4000),
      });
      await refreshIdeas(bookId);
      setHighlightIdeaId(idea.id);
      setError('已存为想法，在右侧。');
    } catch (e) { setError(String(e)); } finally { setBusy(false); }
  };
  const accept = async (draft: AgentDraft) => {
    if (!window.confirm(`接受《${draft.title ?? '未命名'}》并写入正文？`)) return;
    setBusy(true);
    try {
      const edited = draftEdits[draft.id];
      if (edited !== undefined && edited !== draft.content) await updateDraft(draft.id, { content: edited });
      await acceptDraft(draft.id);
      setDraftEdits((prev) => { const next = { ...prev }; delete next[draft.id]; return next; });
      await refreshDrafts(draft.book_id);
    } catch (e) { setError(String(e)); } finally { setBusy(false); }
  };
  const removeIdeaCard = async (idea: AgentIdea) => {
    setBusy(true);
    try {
      await deleteIdea(idea.id);
      setDeletingCardId(null);
      if (expandedCardId === `idea:${idea.id}`) setExpandedCardId(null);
      await refreshIdeas(idea.book_id);
    } catch (e) { setError(String(e)); } finally { setBusy(false); }
  };
  const removeDraftCard = async (draft: AgentDraft) => {
    setBusy(true);
    try {
      await deleteDraft(draft.id);
      setDeletingCardId(null);
      if (expandedCardId === `draft:${draft.id}`) setExpandedCardId(null);
      await refreshDrafts(draft.book_id);
    } catch (e) { setError(String(e)); } finally { setBusy(false); }
  };

  const beginRejectDraft = (draft: AgentDraft) => {    setRejectingDraftId(draft.id);
    setDraftRejectText('');
  };
  const confirmRejectDraft = async (draft: AgentDraft) => {
    setBusy(true);
    try {
      await rejectDraft(draft.id, draftRejectText.trim());
      setRejectingDraftId(null);
      setDraftRejectText('');
      await refreshDrafts(draft.book_id);
    } catch (e) { setError(String(e)); } finally { setBusy(false); }
  };

  const savePersona = async () => {
    if (!persona) return;
    setBusy(true);
    try {
      const next = await updatePersona(persona.id, personaDraft);
      setPersona(next);
      fillPersona(next);
      if (thread) {
        const t = await reloadThread(thread.id);
        setThread(t);
        setMessages(await getThreadMessages(t.id));
        attachStream(t);
      }
      setError('人格已保存并立即生效，对话历史保留。');
    } catch (e) { setError(String(e)); } finally { setBusy(false); }
  };
  const analyzeStyle = async () => {
    if (!bookId) return;
    setBusy(true);
    try {
      const res = await analyzeBookStyle(bookId);
      fillStyle({ ...styleDraft, ...res.profile });
      setError(res.notes.join('；') || '归纳完成，检查后保存。');
    } catch (e) { setError(String(e)); } finally { setBusy(false); }
  };
  const saveStyle = async () => {
    if (!bookId) return;
    setBusy(true);
    try {
      const samples = styleDraft.samples.split('\n').map((s) => s.trim()).filter(Boolean);
      const next = await updateBookStyle(bookId, { ...styleDraft, samples });
      setError('文风已保存。');
      if (thread) {
        const t = await reloadThread(thread.id);
        setThread(t);
        setMessages(await getThreadMessages(t.id));
        attachStream(t);
        setError('文风已保存并立即生效，对话历史保留。');
      }
    } catch (e) { setError(String(e)); } finally { setBusy(false); }
  };
  const loadMemories = async (q = memoryQuery) => {
    if (!bookId) return;
    try { setMemories(await listAgentMemories(bookId, q)); } catch (e) { setError(String(e)); }
  };
  const addMemory = async () => {
    if (!bookId || !memoryText.trim()) return;
    setBusy(true);
    try {
      await createAgentMemory(bookId, { kind: 'preference', content: memoryText.trim(), salience: 4 });
      setMemoryText('');
      await loadMemories();
    } catch (e) { setError(String(e)); } finally { setBusy(false); }
  };
  const saveBudget = async () => {
    if (!bookId || !budget) return;
    const previousFree = budget.freeEdit;
    const previousFull = budget.fullAccess;
    setBusy(true);
    try {
      const next = await updateAgentBudget(bookId, {
        dailyBudgetCny: budget.dailyLimit,
        monthlyBudgetCny: budget.monthlyLimit,
        autonomy: budget.autonomy,
        active: budget.active,
        freeEdit: budget.freeEdit,
        fullAccess: budget.fullAccess,
      });
      setBudget(next);
      if ((previousFree !== next.freeEdit || previousFull !== next.fullAccess) && thread) {
        const t = await reloadThread(thread.id);
        setThread(t);
        setMessages(await getThreadMessages(t.id));
        attachStream(t);
      }
    } catch (e) { setError(String(e)); } finally { setBusy(false); }
  };
  const restartConversation = async () => {
    if (!thread) return;
    setBusy(true);
    try {
      const t = await reloadThread(thread.id);
      setThread(t);
      setMessages(await getThreadMessages(t.id));
      attachStream(t);
      setError('已用最新人格/文风重开对话，历史保留。');
    } catch (e) { setError(String(e)); } finally { setBusy(false); }
  };

  const answerApproval = async (decision: 'allow' | 'deny') => {
    if (!approval || !thread) return;
    setBusy(true);
    try {
      await answerAgentApproval(thread.id, approval.requestId, decision);
      setApproval(null);
      if (decision === 'allow') {
        // execute / edit_chapter 落库后立刻刷新卷章树与当前正文，否则要手动刷新页面才看得到。
        triggerDataRefresh();
        await loadVolumes();
        if (selectedChapter) {
          const cached = dataCache.getChaptersByVolumeRaw(bookId);
          const stillThere = cached
            ? Object.values(cached).flat().find((c) => c.id === selectedChapter.id)
            : undefined;
          if (stillThere) {
            try { await handleSelectChapter(stillThere); } catch { /* 章节可能刚被删除 */ }
          }
        }
      }
    } catch (e) { setError(String(e)); } finally { setBusy(false); }
  };

  const makePairCode = async () => {
    setBusy(true);
    try { setPairCode(await createPairingCode()); } catch (e) { setError(String(e)); } finally { setBusy(false); }
  };
  const doProactive = async () => {
    if (!bookId) return;
    setBusy(true);
    try { await triggerProactive(bookId); setSettingsOpen(false); } catch (e) { setError(String(e)); } finally { setBusy(false); }
  };
  const doRead = async () => {
    const url = window.prompt('网页 URL');
    if (!url) return;
    setBusy(true);
    try { await readWeb(bookId, url); setError('读完了，已放进剪贴簿。'); } catch (e) { setError(String(e)); } finally { setBusy(false); }
  };

  if (!bookId) return <div className="resident-panel"><p className="resident-empty">正在读取作品…</p></div>;

  const visibleDrafts = drafts.filter((d) => d.status === 'pending' || d.status === 'rejected');
  const feed = [
    ...ideas
      .filter((i) => i.status === 'proposed' || i.status === 'approved' || i.status === 'rejected')
      .map((i) => ({ kind: 'idea' as const, created: i.created_at, idea: i })),
    ...visibleDrafts.map((d) => ({ kind: 'draft' as const, created: d.created_at, draft: d })),
  ].sort((a, b) => String(b.created).localeCompare(String(a.created)));
  const query = feedQuery.trim().toLocaleLowerCase();
  const lookupApprovalTarget = (target: string, id: string | undefined): string | undefined => {
    if (!id) return undefined;
    if (target === 'chapter' || target === 'chapter_content') {
      const ch = Object.values(chaptersMap).flat().find((c) => c.id === id);
      return ch ? `第 ${ch.chapter_number} 章《${ch.title}》` : undefined;
    }
    if (target === 'volume') {
      const vol = volumes.find((v) => v.id === id);
      return vol ? `第 ${vol.order_index + 1} 卷《${vol.title}》` : undefined;
    }
    if (target === 'character') return dataCache.getCharacters(bookId)?.find((x) => x.id === id)?.name;
    if (target === 'faction') return dataCache.getFactions(bookId)?.find((x) => x.id === id)?.name;
    if (target === 'setting') return dataCache.getSettings(bookId)?.find((x) => x.id === id)?.title;
    if (target === 'item') return dataCache.getItems(bookId)?.find((x) => x.id === id)?.name;
    return undefined;
  };

  const filteredFeed = feed.filter((item) => {    const isIdea = item.kind === 'idea';
    if (feedFilter === 'idea' && !isIdea) return false;
    if (feedFilter === 'draft' && isIdea) return false;
    if (feedFilter === 'open' && !(item.kind === 'idea' ? item.idea.status === 'proposed' : item.draft.status === 'pending')) return false;
    if (feedFilter === 'rejected' && (item.kind === 'idea' ? item.idea.status : item.draft.status) !== 'rejected') return false;
    if (!query) return true;
    const haystack = item.kind === 'idea'
      ? [item.idea.title, item.idea.summary, item.idea.content, item.idea.rationale].join('\n')
      : [item.draft.title, item.draft.summary, item.draft.content, item.draft.review_comment].join('\n');
    return haystack.toLocaleLowerCase().includes(query);
  });

  return (
    <aside
      className="resident-panel"
      aria-label="余白"
      style={{ '--resident-head-height': `${headHeight}px` } as React.CSSProperties}
    >
      <header className="resident-head">
        <div className="resident-title"><Sparkles size={16} /> 余白</div>
        <div className="resident-book" title={bookTitle}>《{bookTitle || '未命名'}》</div>
        <button
          className={`resident-icon${settingsOpen ? ' is-active' : ''}`}
          title="设置"
          aria-pressed={settingsOpen}
          onClick={() => setSettingsOpen((v) => !v)}
        >
          <Settings size={15} />
        </button>
      </header>

      {error && <div className="resident-notice">{error}</div>}

      <div className="resident-body">
        <section className="resident-chat-col">
          <div className="resident-messages" ref={messagesRef} onScroll={onMessagesScroll}>
            {messages.length === 0 && !streaming && (
              <p className="resident-empty">她知道这本书的设定和章节。先聊卡住的地方，或让她说下一章。</p>
            )}
            {messages.map((m) => (
              <div key={m.id} className={`resident-msg-row ${m.role === 'user' ? 'is-user' : 'is-agent'}`}>
                <div className="resident-msg-time">{time(m.created_at)}</div>
                <div className={`resident-bubble ${m.role === 'user' ? 'is-user' : 'is-agent'}`}>
                  {m.text && <div className="resident-bubble-text"><LinkifiedText text={m.text} /></div>}
                  {m.images?.length ? (
                    <div className="resident-bubble-images">
                      {m.images.map((img, index) => (
                        <img
                          key={index}
                          src={img.dataUrl}
                          alt={img.name ?? '图片'}
                          role="button"
                          tabIndex={0}
                          onClick={() => setImageViewer({ src: img.dataUrl, name: img.name })}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setImageViewer({ src: img.dataUrl, name: img.name }); }}
                        />
                      ))}
                    </div>
                  ) : null}
                  {m.documents?.length ? (
                    <div className="resident-bubble-documents">
                      {m.documents.map((doc, index) => (
                        <span key={index} title={doc.text.slice(0, 500)}>
                          {doc.mimeType === 'application/pdf' || /\.pdf$/i.test(doc.name) ? <FileText size={11} /> : <Paperclip size={11} />} {doc.name}
                          {doc.dataUrl && (
                            <button
                              className="resident-doc-download"
                              title="保存文件"
                              onClick={(e) => {
                                e.stopPropagation();
                                saveChatFile(doc.dataUrl!, doc.name, doc.mimeType);
                              }}
                            >
                              <Download size={11} />
                            </button>
                          )}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
                {m.role === 'assistant' && (
                  <button
                    className="resident-bubble-action"
                    title="把这条回答（或选中的文字）存为想法"
                    onClick={() => void addMessageToIdea(m.text)}
                  >
                    <Lightbulb size={11} /> 存为想法
                  </button>
                )}
              </div>
            ))}
            {streaming && (
              <div className="resident-msg-row is-agent">
                <div className="resident-msg-time">正在写…</div>
                <div className="resident-bubble is-agent"><div className="resident-bubble-text">{streaming}</div></div>
              </div>
            )}
          </div>

          {approval && (
            <div className="resident-approval">
              <ApprovalDetails approval={approval} lookupTarget={lookupApprovalTarget} />
              <div className="resident-card-actions">
                <button className="resident-btn primary" disabled={busy} onClick={() => void answerApproval('allow')}>允许这一次</button>
                <button className="resident-btn" disabled={busy} onClick={() => void answerApproval('deny')}>拒绝</button>
              </div>
            </div>
          )}

          {showJump && (
            <button className="resident-jump" onClick={() => scrollToLatest(true)}>
              回到最新
            </button>
          )}

          {activity && (
            <div className="resident-activity">
              <span className="resident-activity-dot" /> {activity}
            </div>
          )}
          {(pendingImages.length > 0 || pendingDocuments.length > 0) && (
            <div className="resident-composer-images">
              {pendingImages.map((img, index) => (
                <div key={index} className="resident-composer-image">
                  <img src={img.dataUrl} alt={img.name ?? '待发送图片'} />
                  <button title="移除图片" onClick={() => setPendingImages((prev) => prev.filter((_, i) => i !== index))}>×</button>
                </div>
              ))}
              {pendingDocuments.map((doc, index) => (
                <div key={index} className="resident-pending-doc">
                  {doc.mimeType === 'application/pdf' || /\.pdf$/i.test(doc.name) ? <FileText size={12} /> : <Paperclip size={12} />}
                  <span>{doc.name}</span>
                  <button title="移除文档" onClick={() => setPendingDocuments((prev) => prev.filter((_, i) => i !== index))}>×</button>
                </div>
              ))}
            </div>
          )}
          <div className="resident-composer">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              onChange={(e) => void addFiles(e.target.files)}
            />
            <textarea
              ref={inputRef}
              rows={1}
              value={input}
              placeholder="和余白说点什么…（Enter 发送）"
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(); } }}
            />
            <div className="resident-composer-actions">
              <div className="resident-attach-group">
                <button
                  className="resident-attach"
                  title="添加附件（图片/文档，任意格式）"
                  disabled={running || (pendingImages.length >= 4 && pendingDocuments.length >= 4)}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Paperclip size={17} />
                </button>
              </div>
              {running
                ? <button className="resident-send ghost" title="停止" onClick={() => void stop()}><Square size={15} /></button>
                : <button className="resident-send" disabled={(!input.trim() && pendingImages.length === 0 && pendingDocuments.length === 0) || !thread} onClick={() => void send()}><Send size={15} /></button>}
            </div>
          </div>
        </section>

        <ResizeHandle
          direction={-1}
          value={sideWidth}
          min={180}
          onResize={(delta) => setSideWidth((v) => clampSideWidth(v + delta))}
          onReset={() => setSideWidth(SIDE_WIDTH_DEFAULT)}
          label="调整想法/草案区宽度"
          className="resident-split-handle"
        />

        <aside className="resident-side" style={{ width: sideWidth }}>
          <div className="resident-side-head">
            <div>
              <strong>想法与草案</strong>
              <span>{feed.length > 0 ? ` ${filteredFeed.length}/${feed.length}` : ''}</span>
            </div>
            <button className="resident-mini-btn" title="刷新" onClick={() => { void refreshIdeas(bookId); void refreshDrafts(bookId); }}>
              <RefreshCw size={13} />
            </button>
          </div>
          <div className="resident-side-tools">
            <div className="resident-search">
              <Search size={12} />
              <input
                value={feedQuery}
                placeholder="搜索标题或内容"
                onChange={(e) => setFeedQuery(e.target.value)}
              />
              {feedQuery && <button title="清空搜索" onClick={() => setFeedQuery('')}><X size={12} /></button>}
            </div>
            <div className="resident-filter-row">
              {([['all', '全部'], ['idea', '想法'], ['draft', '草案'], ['open', '待处理'], ['rejected', '已拒绝']] as Array<[FeedFilter, string]>).map(([id, label]) => (
                <button key={id} className={feedFilter === id ? 'is-active' : ''} onClick={() => setFeedFilter(id)}>{label}</button>
              ))}
            </div>
          </div>
          <div className="resident-scroll">
            {feed.length === 0 && <p className="resident-empty small">还没有想法或草案。聊到值得留下的内容，点回答下的「存为想法」。</p>}
            {feed.length > 0 && filteredFeed.length === 0 && <p className="resident-empty small">没有匹配的内容。</p>}
            {filteredFeed.map((item) => item.kind === 'idea' ? (
              <article
                key={item.idea.id}
                className={`resident-feed-card is-idea${highlightIdeaId === item.idea.id ? ' is-new' : ''}${expandedCardId === `idea:${item.idea.id}` ? ' is-expanded' : ''}`}
                onClick={(e) => toggleCard(`idea:${item.idea.id}`, e)}
              >
                <div className="resident-card-top">
                  <span className="resident-tag tag-idea">想法</span>
                  {item.idea.status === 'approved' && <span className="resident-tag tag-approved">已认可</span>}
                  {item.idea.status === 'rejected' && <span className="resident-tag tag-rejected">已拒绝</span>}
                  <span className="resident-tag tag-time">{shortTime(item.created)}</span>
                  <button
                    className="resident-card-delete"
                    title="删除这条想法"
                    onClick={(e) => { e.stopPropagation(); setDeletingCardId(`idea:${item.idea.id}`); }}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
                <h4>{item.idea.title}</h4>
                <p className="resident-card-preview">
                  {item.idea.summary || item.idea.content || item.idea.rationale || '（没有摘要）'}
                </p>
                {expandedCardId === `idea:${item.idea.id}` && item.idea.status === 'rejected' && (
                  <p className="resident-hint">拒绝原因：{rejectionReason(item.idea) || '没有写理由'}</p>
                )}
                {deletingCardId === `idea:${item.idea.id}` && (
                  <div className="resident-delete-confirm">
                    <span>删除这条想法？</span>
                    <button className="resident-btn danger" disabled={busy} onClick={(e) => { e.stopPropagation(); void removeIdeaCard(item.idea); }}>删除</button>
                    <button className="resident-btn" disabled={busy} onClick={(e) => { e.stopPropagation(); setDeletingCardId(null); }}>取消</button>
                  </div>
                )}
                {item.idea.status === 'proposed' && (
                  rejectingIdeaId === item.idea.id ? (
                    <div className="resident-reject-box">
                      <textarea
                        rows={2}
                        autoFocus
                        value={rejectReason}
                        placeholder="为什么不合适？（可留空，余白会记住）"
                        onChange={(e) => setRejectReason(e.target.value)}
                      />
                      <div className="resident-card-actions">
                        <button className="resident-btn primary" disabled={busy} onClick={() => void confirmRejectIdea(item.idea)}>确认拒绝</button>
                        <button className="resident-btn" disabled={busy} onClick={() => setRejectingIdeaId(null)}>取消</button>
                      </div>
                    </div>
                  ) : (
                    <div className="resident-card-actions">
                      <button className="resident-btn primary" onClick={() => void approve(item.idea)}><Check size={13} /> 认可</button>
                      <button className="resident-btn" onClick={() => beginRejectIdea(item.idea)}>拒绝</button>
                    </div>
                  )
                )}
                {item.idea.status === 'approved' && <p className="resident-hint">已认可：回聊天说“写吧”。</p>}
              </article>
            ) : (
              <article
                key={item.draft.id}
                className={`resident-feed-card is-draft${expandedCardId === `draft:${item.draft.id}` ? ' is-expanded' : ''}`}
                onClick={(e) => toggleCard(`draft:${item.draft.id}`, e)}
              >
                <div className="resident-card-top">
                  <span className="resident-tag tag-draft">草案</span>
                  {item.draft.status === 'rejected' && <span className="resident-tag tag-rejected">已拒绝</span>}
                  <span className="resident-tag tag-time">{shortTime(item.created)}</span>
                  <span className="resident-time">{item.draft.word_count} 字</span>
                  <button
                    className="resident-card-delete"
                    title="删除这份草案"
                    onClick={(e) => { e.stopPropagation(); setDeletingCardId(`draft:${item.draft.id}`); }}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
                <h4>{item.draft.title ?? '未命名草案'}</h4>
                {expandedCardId === `draft:${item.draft.id}` && item.draft.status === 'pending' ? (
                  <textarea
                    rows={8}
                    className="resident-draft-editor"
                    value={draftEdits[item.draft.id] ?? item.draft.content}
                    onChange={(e) => setDraftEdits((p) => ({ ...p, [item.draft.id]: e.target.value }))}
                  />
                ) : (
                  <p className="resident-card-preview">
                    {item.draft.summary || item.draft.content || '（没有摘要）'}
                  </p>
                )}
                {expandedCardId === `draft:${item.draft.id}` && item.draft.status === 'rejected' && (
                  <p className="resident-hint">拒绝意见：{item.draft.review_comment || '没有写意见'}</p>
                )}
                {deletingCardId === `draft:${item.draft.id}` && (
                  <div className="resident-delete-confirm">
                    <span>删除这份草案？</span>
                    <button className="resident-btn danger" disabled={busy} onClick={(e) => { e.stopPropagation(); void removeDraftCard(item.draft); }}>删除</button>
                    <button className="resident-btn" disabled={busy} onClick={(e) => { e.stopPropagation(); setDeletingCardId(null); }}>取消</button>
                  </div>
                )}
                {item.draft.status === 'pending' && (
                  <>
                    {rejectingDraftId === item.draft.id && (
                      <div className="resident-reject-box">
                        <textarea
                          rows={2}
                          autoFocus
                          value={draftRejectText}
                          placeholder="给余白的意见（可留空）"
                          onChange={(e) => setDraftRejectText(e.target.value)}
                        />
                        <div className="resident-card-actions">
                          <button className="resident-btn primary" disabled={busy} onClick={() => void confirmRejectDraft(item.draft)}>确认拒绝</button>
                          <button className="resident-btn" disabled={busy} onClick={() => setRejectingDraftId(null)}>取消</button>
                        </div>
                      </div>
                    )}
                    <div className="resident-card-actions">
                      <button className="resident-btn primary" disabled={busy} onClick={() => void accept(item.draft)}>接受并写入</button>
                      <button className="resident-btn" disabled={busy} onClick={() => beginRejectDraft(item.draft)}>拒绝</button>
                    </div>
                  </>
                )}
              </article>
            ))}
          </div>
        </aside>
      </div>

      {settingsOpen && (
        <div className="resident-settings-overlay">
          <header className="resident-head">
            <div className="resident-title"><Settings size={15} /> 设置</div>
            <div className="resident-book">《{bookTitle || '未命名'}》</div>
            <button className="resident-back-btn" title="返回聊天" onClick={() => setSettingsOpen(false)}>返回</button>
          </header>
          <div className="resident-settings">
            <nav className="resident-settings-tabs">
              {([['persona', '人格'], ['style', '文风'], ['memory', '记忆'], ['budget', '预算'], ['phone', '手机'], ['actions', '行动']] as Array<[SettingsTab, string]>).map(([id, label]) => (
                <button key={id} className={settingsTab === id ? 'is-active' : ''} onClick={() => {
                  setSettingsTab(id);
                  if (id === 'memory') void loadMemories('');
                  if (id === 'budget') void getAgentBudget(bookId).then(setBudget).catch((e) => setError(String(e)));
                  if (id === 'style') { void getBookStyle(bookId).then((s) => { fillStyle(s); }).catch((): void => undefined); }
                }}>{label}</button>
              ))}
            </nav>
            <div className="resident-settings-body">
              {settingsTab === 'persona' && (
                <>
                  <label className="resident-field"><span>名字</span><input value={personaDraft.name} onChange={(e) => setPersonaDraft((p) => ({ ...p, name: e.target.value }))} /></label>
                  <label className="resident-field"><span>昵称</span><input value={personaDraft.nickname} onChange={(e) => setPersonaDraft((p) => ({ ...p, nickname: e.target.value }))} /></label>
                  <label className="resident-field"><span>身份</span><textarea rows={4} value={personaDraft.identity} onChange={(e) => setPersonaDraft((p) => ({ ...p, identity: e.target.value }))} /></label>
                  <label className="resident-field"><span>说话方式</span><textarea rows={4} value={personaDraft.voice} onChange={(e) => setPersonaDraft((p) => ({ ...p, voice: e.target.value }))} /></label>
                  <label className="resident-field"><span>审美</span><textarea rows={3} value={personaDraft.aesthetics} onChange={(e) => setPersonaDraft((p) => ({ ...p, aesthetics: e.target.value }))} /></label>
                  <label className="resident-field"><span>底线</span><textarea rows={3} value={personaDraft.boundaries} onChange={(e) => setPersonaDraft((p) => ({ ...p, boundaries: e.target.value }))} /></label>
                  <button className="resident-btn primary" disabled={busy} onClick={() => void savePersona()}>保存人格</button>
                </>
              )}
              {settingsTab === 'style' && (
                <>
                  {(['pov', 'tense', 'paragraph', 'dialogue', 'vocabulary', 'banned', 'samples'] as const).map((key) => (
                    <label key={key} className="resident-field">
                      <span>{key === 'pov' ? '人称/视角' : key === 'tense' ? '时态' : key === 'paragraph' ? '段落节奏' : key === 'dialogue' ? '对白习惯' : key === 'vocabulary' ? '用词偏好' : key === 'banned' ? '避免表达' : '样例句（每行一句）'}</span>
                      <textarea rows={key === 'samples' ? 3 : 2} value={styleDraft[key] ?? ''} onChange={(e) => setStyleDraft((p) => ({ ...p, [key]: e.target.value }))} />
                    </label>
                  ))}
                  <div className="resident-card-actions">
                    <button className="resident-btn" disabled={busy} onClick={() => void analyzeStyle()}>从章节归纳</button>
                    <button className="resident-btn primary" disabled={busy} onClick={() => void saveStyle()}>保存</button>
                  </div>
                </>
              )}
              {settingsTab === 'memory' && (
                <>
                  <div className="resident-memory-search">
                    <input value={memoryQuery} placeholder="搜记忆…" onChange={(e) => setMemoryQuery(e.target.value)} />
                    <button className="resident-mini-btn" onClick={() => void loadMemories(memoryQuery)}>搜索</button>
                  </div>
                  {memories.map((m) => (
                    <div key={m.id} className="resident-memory-row">
                      <span>{m.kind}</span>
                      <p>{m.content}</p>
                      <button className="resident-mini-btn" disabled={busy} onClick={() => void archiveAgentMemory(m.id).then(() => loadMemories())}>归档</button>
                    </div>
                  ))}
                  <div className="resident-memory-add">
                    <textarea rows={2} value={memoryText} placeholder="记一条偏好/决定…" onChange={(e) => setMemoryText(e.target.value)} />
                    <button className="resident-btn primary" disabled={busy || !memoryText.trim()} onClick={() => void addMemory()}>记下来</button>
                  </div>
                </>
              )}
              {settingsTab === 'budget' && budget && (
                <>
                  <p className="resident-hint">今日 ¥{budget.dailySpent}/{budget.dailyLimit} · 本月 ¥{budget.monthlySpent}/{budget.monthlyLimit}</p>
                  <label className="resident-field"><span>每日预算（元，0=急停）</span><input type="number" min={0} value={budget.dailyLimit} onChange={(e) => setBudget({ ...budget, dailyLimit: Number(e.target.value) })} /></label>
                  <label className="resident-field"><span>每月预算（元）</span><input type="number" min={0} value={budget.monthlyLimit} onChange={(e) => setBudget({ ...budget, monthlyLimit: Number(e.target.value) })} /></label>
                  <label className="resident-field"><span>自主等级（0–3）</span><input type="number" min={0} max={3} value={budget.autonomy} onChange={(e) => setBudget({ ...budget, autonomy: Number(e.target.value) })} /></label>
                  <label className="resident-inline"><input type="checkbox" checked={budget.active} onChange={(e) => setBudget({ ...budget, active: e.target.checked })} />启用驻场作家</label>
                  <label className="resident-inline">
                    <input type="checkbox" checked={budget.freeEdit} onChange={(e) => setBudget({ ...budget, freeEdit: e.target.checked })} />
                    自由编辑（经你审批后可直接改正文；不能删除）
                  </label>
                  <label className="resident-inline">
                    <input type="checkbox" checked={budget.fullAccess} onChange={(e) => setBudget({ ...budget, fullAccess: e.target.checked })} />
                    完全授权（可增删改所有创作数据；每次仍审批）
                  </label>
                  <p className="resident-hint">关闭时所有改动只能走 Idea → 草案 → 你接受；打开后她才能调用 edit_chapter，且每次仍需审批。完全授权会注册通用 execute：章节、人物、势力、设定、物品、卷、大纲都能增删改；开启前请先运行备份脚本。</p>
                  <button className="resident-btn primary" disabled={busy} onClick={() => void saveBudget()}>保存</button>
                </>
              )}
              {settingsTab === 'phone' && (
                <>
                  <p className="resident-hint">手机打开 <code>{`${window.location.origin}/#/m`}</code>，输入配对码；模拟器可用 10.0.2.2:5173。</p>
                  {pairCode && <div className="resident-pair-code">{pairCode.code}</div>}
                  <button className="resident-btn primary" disabled={busy} onClick={() => void makePairCode()}>生成配对码</button>
                </>
              )}
              {settingsTab === 'actions' && (
                <div className="resident-card-actions vertical">
                  <button className="resident-btn" disabled={busy || !thread} onClick={() => void restartConversation()}>重开对话（保留历史）</button>
                  <button className="resident-btn" disabled={busy} onClick={() => void doProactive()}>自己翻翻</button>
                  <button className="resident-btn" disabled={busy} onClick={() => void doRead()}>给她读一篇</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {imageViewer && (
        <ImageViewer src={imageViewer.src} name={imageViewer.name} onClose={() => setImageViewer(null)} />
      )}
    </aside>
  );
}

